/**
 * Finds the lines of text already on a page (from pdf.js text items) so the PDF Editor can let people click one
 * and change it. Positions are fractions of the page as displayed (0..1, y downwards).
 */
export interface RawTextItem { str: string; transform: number[]; width: number }
export interface ViewportLike { transform: number[]; width: number; height: number }

export interface TextLine {
  /** Words of the line, as one string. */
  text: string;
  /** Box around the line, fractions of the displayed page. */
  x: number; y: number; w: number; h: number;
  /** Baseline, fraction of the page height (y downwards). */
  baseline: number;
  /** Font size in points. */
  size: number;
}

const mul = (m1: number[], m2: number[]) => [
  m1[0] * m2[0] + m1[2] * m2[1], m1[1] * m2[0] + m1[3] * m2[1],
  m1[0] * m2[2] + m1[2] * m2[3], m1[1] * m2[2] + m1[3] * m2[3],
  m1[0] * m2[4] + m1[2] * m2[5] + m1[4], m1[1] * m2[4] + m1[3] * m2[5] + m1[5],
];

/** Horizontal text only. Items on one baseline that sit close together (a gap under about one em) form one line. */
export function findTextLines(items: RawTextItem[], vp: ViewportLike): TextLine[] {
  type P = { str: string; x: number; right: number; base: number; size: number };
  const parts: P[] = [];
  for (const it of items) {
    if (!it.str || !it.str.trim() && it.str.length === 0) continue;
    const t = mul(vp.transform, it.transform);
    const size = Math.hypot(t[2], t[3]);
    // Turned or mirrored text is left alone: a straight box would not cover it.
    if (size < 3 || Math.abs(t[1]) > 0.02 * Math.abs(t[0]) || t[0] <= 0) continue;
    const width = it.width * Math.hypot(vp.transform[0], vp.transform[1]);
    parts.push({ str: it.str, x: t[4], right: t[4] + width, base: t[5], size });
  }
  parts.sort((a, b) => (Math.abs(a.base - b.base) < Math.min(a.size, b.size) * 0.3 ? a.x - b.x : a.base - b.base));
  const lines: P[][] = [];
  for (const p of parts) {
    const cur = lines[lines.length - 1];
    const last = cur?.[cur.length - 1];
    if (last && Math.abs(last.base - p.base) < Math.min(last.size, p.size) * 0.3 && p.x - last.right < Math.max(last.size, p.size) * 1.0 && p.x >= last.x) cur.push(p);
    else lines.push([p]);
  }
  const out: TextLine[] = [];
  for (const l of lines) {
    let text = "";
    for (let i = 0; i < l.length; i++) {
      const gap = i > 0 ? l[i].x - l[i - 1].right : 0;
      if (i > 0 && gap > l[i].size * 0.15 && !text.endsWith(" ") && !l[i].str.startsWith(" ")) text += " ";
      text += l[i].str;
    }
    text = text.replace(/\s+$/, "");
    if (!text.trim()) continue;
    const size = Math.max(...l.map((p) => p.size));
    const base = l[0].base;
    const x0 = l[0].x, x1 = l[l.length - 1].right;
    const top = base - size * 0.95, h = size * 1.25;
    out.push({ text, x: x0 / vp.width, y: top / vp.height, w: (x1 - x0) / vp.width, h: h / vp.height, baseline: base / vp.height, size });
  }
  return out;
}
